/**
 * @fileoverview Home page component that renders profile-specific dashboards
 * Based on user permissions, shows the appropriate dashboard for each profile type
 * Uses priority-based selection to show only ONE dashboard per user
 * Shows OnboardingPage for users with pending status
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { havePermissions, fetchData, intl, HTTP_STATUS } from "dmsbasereact";
import { Box, CircularProgress, Typography, Alert, Button } from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";

// Dashboard imports
import { DoctorDashboard } from "../Dashboard/DoctorDashboard";
import { PharmacistDashboard } from "../Dashboard/PharmacistDashboard";
import { AdminDashboard } from "../Dashboard/AdminDashboard";
import { InstitutionResponsibleDashboard } from "../Dashboard/InstitutionResponsibleDashboard";
import { EurofarmaGestaoLenaDashboard } from "../Dashboard/EurofarmaGestaoLenaDashboard";
import { EurofarmaDiretoriaDashboard } from "../Dashboard/EurofarmaDiretoriaDashboard";
import { EurofarmaRegulatoriooDashboard } from "../Dashboard/EurofarmaRegulatoriooDashboard";
import { EurofarmaComercialDashboard } from "../Dashboard/EurofarmaComercialDashboard";
import { EurofarmaCentralDashboard } from "../Dashboard/EurofarmaCentralDashboard";
import { EurofarmaQualityDashboard } from "../Dashboard/EurofarmaQualityDashboard";
import { AuditorDashboard } from "../Dashboard/AuditorDashboard";
import { OnboardingPage } from "../Onboarding";

import { Permissions } from "../../constants/Permissions";

// Account status constants
const ACCOUNT_STATUS = {
    PENDING_CFM_VALIDATION: 1,
    PENDING_DOCUMENT_UPLOAD: 2,
    PENDING_APPROVAL: 3,
    PENDING_TRAINING: 4,
    ACTIVE: 5,
    REJECTED: 6,
    SUSPENDED: 7,
    PENDING_APPROVAL_GESTAO_LENA: 8,
    PENDING_APPROVAL_REGULATORIO: 9,
    PENDING_RECYCLING_TRAINING: 10,
};

// Status that require onboarding flow (or special handling)
// NOTE: PENDING_RECYCLING_TRAINING users should see dashboard with alert banner, not onboarding
const ONBOARDING_STATUSES = [
    ACCOUNT_STATUS.PENDING_CFM_VALIDATION,
    ACCOUNT_STATUS.PENDING_DOCUMENT_UPLOAD,
    ACCOUNT_STATUS.PENDING_APPROVAL,
    ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA,
    ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO,
    ACCOUNT_STATUS.PENDING_TRAINING,
    ACCOUNT_STATUS.REJECTED,
    ACCOUNT_STATUS.SUSPENDED,
];

// Truly pending professional profile statuses (for "pending types" banner)
// These are statuses where the profile is still going through registration/approval
// Excludes: ACTIVE (completed), REJECTED/SUSPENDED (failed/blocked), PENDING_RECYCLING_TRAINING (was active, needs retraining)
const PENDING_PROFILE_STATUSES = [
    ACCOUNT_STATUS.PENDING_CFM_VALIDATION,
    ACCOUNT_STATUS.PENDING_DOCUMENT_UPLOAD,
    ACCOUNT_STATUS.PENDING_APPROVAL,
    ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA,
    ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO,
    ACCOUNT_STATUS.PENDING_TRAINING,
];

/**
 * Dashboard priority configuration
 * First matching dashboard (by permission) will be rendered
 * Order matters: more specific roles should come before generic ones
 */
const DASHBOARD_CONFIG = [
    {
        key: "admin",
        permission: Permissions.LENA_ADMIN_ACCESS,
        Component: AdminDashboard,
    },
    {
        key: "doctor",
        permission: Permissions.DOCTOR_PAGES_ACCESS,
        Component: DoctorDashboard,
        requiresProfileCheck: "isDoctor",
    },
    {
        key: "pharmacist",
        permission: Permissions.PHARMACIST_PAGES_ACCESS,
        Component: PharmacistDashboard,
        requiresProfileCheck: "isPharmacist",
    },
    {
        key: "institution",
        permission: Permissions.INSTITUTION_RESPONSIBLE_ACCESS,
        Component: InstitutionResponsibleDashboard,
    },
    {
        key: "eurofarmaManagement",
        permission: Permissions.EUROFARMA_MANAGEMENT,
        Component: EurofarmaGestaoLenaDashboard,
    },
    {
        key: "eurofarmaDiretoria",
        permission: Permissions.EUROFARMA_SIGNING,
        Component: EurofarmaDiretoriaDashboard,
    },
    {
        key: "eurofarmaRegulatorio",
        permission: Permissions.EUROFARMA_REGULATORY,
        Component: EurofarmaRegulatoriooDashboard,
    },
    {
        key: "eurofarmaComercial",
        permission: Permissions.EUROFARMA_COMMERCIAL,
        Component: EurofarmaComercialDashboard,
    },
    {
        key: "eurofarmaCentral",
        permission: Permissions.EUROFARMA_CENTRAL_READONLY,
        Component: EurofarmaCentralDashboard,
    },
    {
        key: "eurofarmaQuality",
        permission: Permissions.EUROFARMA_QUALITY_READONLY,
        Component: EurofarmaQualityDashboard,
    },
    {
        key: "auditor",
        permission: Permissions.EUROFARMA_READONLY,
        Component: AuditorDashboard,
    },
];

/**
 * HomePage - Main home page with role-based dashboard routing
 * Determines user profile and renders appropriate dashboard
 * Uses priority-based selection to show only ONE dashboard
 * Shows OnboardingPage for users with pending status
 */
export const HomePage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState({
        isDoctor: false,
        isPharmacist: false
    });
    const [accountStatus, setAccountStatus] = useState(null);
    const [statusReason, setStatusReason] = useState(null);
    const [userType, setUserType] = useState(null);
    const [userTypes, setUserTypes] = useState([]);  // Multi-type support
    const [isExternalUser, setIsExternalUser] = useState(false);

    // Training expiration warning state
    const [trainingExpirationInfo, setTrainingExpirationInfo] = useState(null);
    const [showTrainingExpirationWarning, setShowTrainingExpirationWarning] = useState(false);

    // Training completion state (for ACTIVE users who haven't completed training)
    const [trainingRequired, setTrainingRequired] = useState(false);
    const [trainingCompleted, setTrainingCompleted] = useState(true); // default true to avoid flicker

    /**
     * Check user profile by calling backend endpoints
     */
    /**
     * Fetch user profile data. When silent=true, skips the loading spinner
     * to avoid full-page flicker during background polling.
     */
    const fetchUserProfile = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [doctorResponse, pharmacistResponse, accountResponse] = await Promise.all([
                fetchData("/prescription/check_doctor", "GET", true).catch(() => null),
                fetchData("/dashboard/pharmacist/check", "GET", true).catch(() => null),
                fetchData("/account/status", "GET", true).catch(() => null)
            ]);

            setUserProfile({
                isDoctor: doctorResponse?.data?.is_doctor === true,
                isPharmacist: pharmacistResponse?.data?.data?.is_pharmacist === true
            });

            if (accountResponse?.status === HTTP_STATUS.OK_200) {
                const status = accountResponse.data?.status;
                setAccountStatus(status);
                setStatusReason(accountResponse.data?.status_reason);
                setUserType(accountResponse.data?.user_type);
                setUserTypes(accountResponse.data?.types || []);  // Multi-type support
                const isAuditor = havePermissions(Permissions.EUROFARMA_READONLY);
                setIsExternalUser(!isAuditor);

                // Check training completion for all users (not just ACTIVE)
                // Training is person-level, separate from institution approval
                // Training check should run regardless of status to properly show onboarding
                try {
                    const trainingResponse = await fetchData('/my-training-status', 'GET', true);
                    const trainingData = trainingResponse?.data || trainingResponse;
                    const completed = trainingData?.training_completed || false;

                    setTrainingCompleted(completed);
                    // Training is required if user has at least one ACTIVE institution but hasn't completed training
                    setTrainingRequired(status === ACCOUNT_STATUS.ACTIVE && !completed);
                } catch (trainingError) {
                    // On error, assume training is completed to avoid blocking
                    setTrainingRequired(false);
                    setTrainingCompleted(true);
                }
            }
        } catch (err) {
            // Error checking user profile - continue with defaults
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Initial load (with spinner)
    const checkUserProfile = useCallback(() => fetchUserProfile(false), [fetchUserProfile]);

    // Silent poll (no spinner) - used by onboarding polling
    const pollUserProfile = useCallback(() => fetchUserProfile(true), [fetchUserProfile]);

    useEffect(() => {
        checkUserProfile();
    }, [checkUserProfile]);

    // Check training expiration for controlled roles
    const checkTrainingExpiration = useCallback(async () => {
        try {
            const response = await fetchData('/training_material/check-completion?recycling=true', 'GET', true);
            const data = response.data || response;

            if (data.requires_training && data.completed && data.expiring_soon) {
                setTrainingExpirationInfo({
                    daysRemaining: data.days_until_expiration,
                    validUntil: data.certificate_valid_until,
                    trainingName: data.training_name
                });
                setShowTrainingExpirationWarning(true);
            } else {
                setShowTrainingExpirationWarning(false);
            }
        } catch (error) {
            // On error, don't show warning
            setShowTrainingExpirationWarning(false);
        }
    }, []);

    // Listen for training completion to refresh status
    useEffect(() => {
        const handleTrainingCompleted = () => {
            checkUserProfile();
            checkTrainingExpiration();
        };
        window.addEventListener('trainingCompleted', handleTrainingCompleted);
        return () => {
            window.removeEventListener('trainingCompleted', handleTrainingCompleted);
        };
    }, [checkUserProfile, checkTrainingExpiration]);

    useEffect(() => {
        checkTrainingExpiration();
    }, [checkTrainingExpiration]);

    /**
     * Find the first matching dashboard based on permissions and profile checks
     * Returns the dashboard component to render
     */
    const activeDashboard = useMemo(() => {
        for (const config of DASHBOARD_CONFIG) {
            if (!havePermissions(config.permission)) {
                continue;
            }

            if (config.requiresProfileCheck) {
                if (!userProfile[config.requiresProfileCheck]) {
                    continue;
                }
            }

            return config;
        }
        return null;
    }, [userProfile]);

    // Check if user needs onboarding
    // Show onboarding if:
    // 1. User has pending status (current behavior), OR
    // 2. User is ACTIVE but training is required and not completed
    const needsTraining = trainingRequired && !trainingCompleted;
    const needsOnboarding = isExternalUser && (
        ONBOARDING_STATUSES.includes(accountStatus) ||
        (accountStatus === ACCOUNT_STATUS.ACTIVE && needsTraining)
    );

    // Check if user has pending type requests (for multi-type users)
    // Only show truly pending statuses (in registration/approval flow)
    // Exclude PENDING_RECYCLING_TRAINING (was active, needs retraining - handled by training alert banner)
    const pendingTypes = useMemo(() => {
        if (!userTypes || userTypes.length === 0) return [];
        return userTypes.filter(t => PENDING_PROFILE_STATUSES.includes(t.status));
    }, [userTypes]);

    const hasPendingTypes = pendingTypes.length > 0;

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh',
                    gap: 2
                }}
            >
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                    {intl.get("LoadingDashboard").d("Loading dashboard...")}
                </Typography>
            </Box>
        );
    }

    // Show onboarding for users with pending status or pending training
    if (needsOnboarding) {
        return <OnboardingPage accountStatus={accountStatus} userType={userType} userTypes={userTypes} statusReason={statusReason} onStatusChange={checkUserProfile} onPoll={pollUserProfile} needsTraining={needsTraining} />;
    }

    if (!activeDashboard) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh'
                }}
            >
                <Typography variant="body1" color="text.secondary">
                    {intl.get("NoDashboardAvailable").d("No dashboard available for your profile")}
                </Typography>
            </Box>
        );
    }

    const DashboardComponent = activeDashboard.Component;

    // Render training expiration warning banner
    const renderTrainingExpirationBanner = () => {
        if (!showTrainingExpirationWarning || !trainingExpirationInfo) return null;

        return (
            <Alert
                severity="warning"
                sx={{ mb: 2 }}
                onClose={() => setShowTrainingExpirationWarning(false)}
            >
                <strong>{intl.get("training_expiring_soon").d("Training certificate expiring soon!")}</strong>
                <br />
                {intl.get("training_expires_in_days").d("Your training certificate expires in {days} days.").replace("{days}", trainingExpirationInfo.daysRemaining)}
                {" "}
                {intl.get("training_renewal_reminder").d("Please renew your training before the expiration date.")}
            </Alert>
        );
    };

    // Render pending types banner
    const renderPendingTypesBanner = () => {
        if (!hasPendingTypes) return null;

        const pendingTypeNames = pendingTypes.map(t => {
            switch (t.type) {
                case 'doctor': return intl.get("Doctor").d("Doctor");
                case 'pharmacist': return intl.get("Pharmacist").d("Pharmacist");
                case 'institution_responsible': return intl.get("InstitutionResponsible").d("Institution Responsible");
                default: return t.type;
            }
        }).join(', ');

        return (
            <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                    <Button
                        color="inherit"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => navigate('/profile-settings?tab=profiles')}
                    >
                        {intl.get("GoToProfileSettings").d("Go to Profile Settings")}
                    </Button>
                }
            >
                {intl.get("PendingTypesNotice").d("You have pending professional profiles: {types}. Complete the registration process to activate them.").replace('{types}', pendingTypeNames)}
            </Alert>
        );
    };

    return (
        <Box>
            {renderTrainingExpirationBanner()}
            {renderPendingTypesBanner()}
            <DashboardComponent />
        </Box>
    );
};
